import type { ThemeMode } from '@shared/app-config/types'

/**
 * Applies a forced theme mode to an element (default <html>) by toggling the
 * `w-theme-dark` / `w-theme-light` classes that theme-config.css and the Tailwind
 * `dark:` variant respond to, and setting `color-scheme` for native UI.
 *
 * - 'auto'  → no class; `color-scheme` falls back to the stylesheet (`light dark`).
 * - 'light' / 'dark' → forces tokens, `dark:` utilities, and native controls.
 *
 * Can be applied to any element (not just <html>) for subtree theming.
 */
export function applyThemeMode(
  mode: ThemeMode | undefined,
  root: HTMLElement = document.documentElement,
): void {
  // Clamp anything that isn't a valid forced mode (undefined, 'auto', or junk
  // from a misconfigured facade/parent) to 'auto' — never let a bad value reach
  // classList / colorScheme as an invalid token that silently no-ops.
  const resolved: ThemeMode = (mode === 'dark' || mode === 'light') ? mode : 'auto'
  root.classList.toggle('w-theme-dark', resolved === 'dark')
  root.classList.toggle('w-theme-light', resolved === 'light')
  if (resolved === 'auto')
    root.style.removeProperty('color-scheme')
  else
    root.style.colorScheme = resolved
}
